import { useApiPost, useApiQuery } from '@/api/main/query'
import { PaginatedCollection } from '@/api/types'
import { usePaymentRequisitActions } from '@/entities/paymentRequisit'
import { EnrichedRequisitesTable } from '@/features/baseTable/ui/EnrichedRequisitesTable'
import {
  DialogEditRequisit,
  EnrichedRequisitesModel,
  RequisiteMobileBlock,
  RequisiteStatisticModel,
  RequisitesHeader,
  detailChangeStatus,
  invalidateAllRequisiteData,
  requisitColumns,
  requisiteAPI,
  requisiteStatisticApi,
  useEditRequisit,
} from '@/features/requisites'
import { DEFAULT_TAKE } from '@/shared/constants/constants'
import { useDebounce, useToast } from '@/shared/hooks'
import { useQueryClient } from '@tanstack/react-query'
import { Table } from '@tanstack/react-table'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { RequisiteModel, UpdateRequisitesStatus } from '../../model/types'
import { BaseTable, BaseTableSkeleton } from 'syncra-storybook/features'

type Props = {
  farmPage?: boolean
  farmId?: string
}

const detailFilterButtons = [
  { name: 'Все', label: 'all', index: 0 },
  { name: 'Активные', label: 'active', index: 1 },
  { name: 'Приостановленые', label: 'disabled', index: 2 },
  { name: 'Заблокированные', label: 'blocked', index: 4 },
]

export const RequisitesTable = ({ farmPage, farmId }: Props) => {
  const [search, setSearch] = useState('')
  const [selectedBtn, setSelectedBtn] = useState('all')
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [requisiteStatus, setRequisiteStatus] = useState(0)
  const [modalEditRequisiteVisible, setModalEditRequisiteVisible] = useState(false)
  const [activePaymentRequisite, setActivePaymentRequisite] = useState<string>('')
  const [requisitId, setRequisitId] = useState('')
  const [skip, setSkip] = useState(0)
  const [tableInstance, setTableInstance] = useState<Table<RequisiteModel> | null>(null)
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null)
  const debouncedSearch = useDebounce(search, 500)
  const debouncedSkip = useDebounce(skip, 500)
  const effectiveSkip = skip === 0 ? 0 : debouncedSkip

  const queryClient = useQueryClient()
  const { addToast } = useToast()

  const toggleRowSelection = (id: string) => {
    setSelectedIds((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }

  const handleSuccessAction = useCallback(() => {
    setModalEditRequisiteVisible(false)
    setRequisitId('')
  }, [])

  const {
    data: requisitesData,
    error: requisitesError,
    isLoading: requisitesIsLoad,
  } = useApiQuery<PaginatedCollection<RequisiteModel[]>>(
    ['requisites', effectiveSkip, DEFAULT_TAKE, debouncedSearch, requisiteStatus, farmId],
    farmPage ? requisiteAPI.farm : requisiteAPI.baseKey,
    {
      skip: effectiveSkip,
      take: DEFAULT_TAKE,
      farmId,
      status: requisiteStatus,
      textFilter: debouncedSearch,
    }
  )

  const currentDate = new Date().toISOString().split('T')[0] + 'T00:00:00.000Z'
  const ids = useMemo(() => requisitesData?.items?.map((r) => r.id) ?? [], [requisitesData?.items])
  const queryString = ids.map((id) => `ids=${encodeURIComponent(id)}`).join('&')

  const {
    data: dailyStatsData,
    isLoading: isStatsLoading,
    error: statsError,
  } = useApiQuery<PaginatedCollection<RequisiteStatisticModel[]>>(
    ['requisite-daily-stat', ids],
    requisiteStatisticApi.getDailyStat(queryString),
    { date: currentDate },

    { enabled: ids.length > 0 }
  )

  const {
    data: totalStatsData,
    isLoading: isTotalStatsLoading,
    error: totalStatsError,
  } = useApiQuery<PaginatedCollection<RequisiteStatisticModel[]>>(
    ['requisite-total-stat', ids],
    requisiteStatisticApi.getTotalStat(queryString),
    { date: currentDate },
    { enabled: ids.length > 0 }
  )

  const enrichedData: EnrichedRequisitesModel[] = (requisitesData?.items ?? []).map((req) => ({
    ...req,
    dailyStats: dailyStatsData?.items?.filter((stat) => stat.requisitId === req.id) ?? [],
    totalStats: totalStatsData?.items?.filter((stat) => stat.requisitId === req.id) ?? [],
  }))

  const {
    data: queryRequisitData,
    error: requisitError,
    refetch: refetchRequisitData,
  } = useApiQuery<RequisiteModel>(
    ['requisit', requisitId],
    requisiteAPI.getRequisit(requisitId),
    undefined,
    {
      enabled: false,
    }
  )

  const { disablePaymentRequisit, enablePaymentRequisit } = usePaymentRequisitActions(
    [],
    activePaymentRequisite
  )

  const { handleDeleteRequisit, handleUpdateRequisitSubmit } = useEditRequisit(
    queryRequisitData,
    requisitId,
    handleSuccessAction
  )

  const { mutate: updateRequsitesStatus } = useApiPost<
    void,
    {
      guids: string[]
      status: UpdateRequisitesStatus
      reason?: string
    }
  >(requisiteAPI.updateRequsitesStatus, {
    onSuccess: () => {
      addToast('Успех!', 'Статус реквизитов успешно обновлен.', 'success')
      invalidateAllRequisiteData(queryClient, requisitId)

      setSelectedIds(new Set())
      tableInstance?.resetRowSelection()
    },
    onError: (error) => {
      addToast('Ошибка!', error.message, 'error')
    },
  })

  useEffect(() => {
    if (requisitesError) {
      addToast('Ошибка!', requisitesError.message, 'error')
    }
  }, [requisitesError])

  useEffect(() => {
    if (requisitError) {
      addToast('Ошибка!', requisitError.message, 'error')
    }
  }, [requisitError])

  useEffect(() => {
    if (requisitId && requisitId.trim() !== '') {
      refetchRequisitData().then((res) => {
        if (res.data) {
          setModalEditRequisiteVisible(true)
        }
      })
    }
  }, [requisitId, refetchRequisitData])

  const handleOpenModal = (id: string) => {
    if (!id || id.trim() === '') {
      addToast('Ошибка!', 'Некорректный ID реквизита', 'error')
      return
    }
    setRequisitId(id)
  }

  const handleCloseModal = (value: boolean) => {
    setModalEditRequisiteVisible(value)
    setRequisitId('')
  }

  const handleDisablePaymentRequisit = (id: string) => {
    setActivePaymentRequisite(id)
    disablePaymentRequisit(id)
  }

  const handleEnablePaymentRequisit = (id: string) => {
    setActivePaymentRequisite(id)
    enablePaymentRequisit(id)
  }

  const valueToIndexMap = Object.fromEntries(
    detailChangeStatus.map((opt) => [opt.value, opt.index])
  )

  const handleUpdateStatus = (value: string) => {
    const index = valueToIndexMap[value]
    if (index === undefined) return

    const selectedFromTable =
      tableInstance?.getSelectedRowModel().rows.map((row) => row.original.id) || []
    const selectedMobile = Array.from(selectedIds)
    const selected = selectedFromTable.length > 0 ? selectedFromTable : selectedMobile

    if (!selected.length) {
      addToast('Ошибка!', 'Сначала выберите хотя бы один реквизит', 'error')
      setSelectedStatus(null)
      return
    }

    updateRequsitesStatus({ guids: selected, status: index })
    setSelectedStatus(null)
  }

  return (
    <div className="flex flex-col gap-4">
      <RequisitesHeader
        selectedBtn={selectedBtn}
        selectFilterBtn={(label) => {
          setSelectedBtn(label)
          const selectedButton = detailFilterButtons.find((b) => b.label === label)
          if (selectedButton) {
            setRequisiteStatus(selectedButton.index)
          }
        }}
        setSearch={setSearch}
        selectedStatus={selectedStatus}
        setSelectedStatus={setSelectedStatus}
        onChangeStatus={(value) => {
          if (typeof value === 'string') {
            handleUpdateStatus(value)
          }
        }}
        setSkip={setSkip}
      />

      {requisitesError || statsError || totalStatsError ? (
        <p>Ошибка загрузки данных.</p>
      ) : requisitesIsLoad || isStatsLoading || isTotalStatsLoading ? (
        <BaseTableSkeleton />
      ) : !requisitesData?.items || requisitesData.items.length === 0 ? (
        <p>Реквизитов нет.</p>
      ) : (
        <BaseTable
          columns={requisitColumns({ onEdit: handleOpenModal })}
          data={enrichedData}
          onPageChange={(newSkip) => setSkip(newSkip)}
          cardVariant="primary"
          cardClasses="border-none rounded-none flex flex-col gap-4"
          skip={skip}
          totalItems={requisitesData.count}
          isRowLink
          navigateTo="requisites"
          onTableReady={(table) => setTableInstance(table)}
          mobileCards={enrichedData.map((detail) => (
            <RequisiteMobileBlock
              key={detail.id}
              requisit={detail}
              isSelected={selectedIds.has(detail.id)}
              onSelect={() => toggleRowSelection(detail.id)}
              onEdit={handleOpenModal}
            />
          ))}
          expandableRows
          expandableChildren={(row) => (
            <EnrichedRequisitesTable
              requisit={row}
              onDisable={handleDisablePaymentRequisit}
              onEnable={handleEnablePaymentRequisit}
            />
          )}
        />
      )}
      {modalEditRequisiteVisible && (
        <DialogEditRequisit
          modalVisible={modalEditRequisiteVisible}
          setModalVisible={handleCloseModal}
          onSubmit={handleUpdateRequisitSubmit}
          onDelete={handleDeleteRequisit}
          requisitData={queryRequisitData}
        />
      )}
    </div>
  )
}
